import { OfficeStatus } from "@prisma/client";
import type { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { z } from "zod";

import { validateEmailAddress } from "@calcom/lib/email";
import prisma from "@calcom/prisma";
import { trpc } from "@calcom/trpc/react";
import type { inferSSRProps } from "@calcom/types/inferSSRProps";
import { Button, TextField } from "@calcom/ui";

import { toBrandedWrapperBrand } from "@lib/patient/toBrandedWrapperBrand";
import { formatPhoneNumber } from "@lib/practice/helper";

import BrandedComponentWrapper from "@components/forms/components/BrandedComponentWrapper";
import { HaveQuestionCard } from "@components/patient/HaveQuestionCard";

type StatementPageProps = inferSSRProps<typeof getServerSideProps>;

const querySchema = z.object({
  user: z.string(),
});

export default function OtpRequest({ office }: StatementPageProps) {
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isPhoneRequestSent, setIsPhoneRequestSent] = useState(false);
  const [isEmailRequestSent, setIsEmailRequestSent] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const requestCode = trpc.viewer.payments.sendOtpForOnlinePayment.useMutation({
    onSuccess: ({ mode }) => {
      setErrorMessage("");
      if (mode == "phone") {
        setIsPhoneRequestSent(true);
      } else {
        setIsEmailRequestSent(true);
      }
    },
    onError: (error: any) => {
      setErrorMessage(error?.message);
    },
  });

  const verifyCode = trpc.viewer.payments.verifyOtpForOnlinePayment.useMutation({
    onSuccess: (data) => {
      router.push({
        pathname: data.statementLink,
        query: {
          uid: data.patientId,
          user: data.officeName,
        },
      });
    },
    onError: (error: any) => {
      setErrorMessage(error?.message);
    },
  });

  return (
    <BrandedComponentWrapper
      brand={toBrandedWrapperBrand(office)}
      securityNote="Payments are secured using TLS & SSL encryption.">
      <div className="grid grow grid-cols-1 border-t border-gray-200 md:grid-cols-7">
        <div className="col-span-4 flex flex-col gap-5 border-gray-200 bg-white py-8 px-1 pb-[60px] text-sm md:border-r md:px-8">
          <div className="px-2 md:px-0">
            <h2 className="mb-2 text-xl font-bold text-gray-800">Look up your statement link.</h2>
            <p className="text-sm leading-6 text-gray-600">
              Enter your phone number in the field below to access your statement. After submitting your
              number, you&apos;ll receive a verification code. Once you enter the code, you&apos;ll be able to
              view your statement, including a detailed breakdown of your ledger and outstanding balance.
            </p>
          </div>

          <div className="flex w-full gap-2 md:w-96">
            <TextField
              placeholder="Input your phone number"
              containerClassName="grow"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
              addOnLeading="+1"
            />
            <Button
              disabled={phoneNumber.length < 13}
              className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
              onClick={() => {
                requestCode.mutate({
                  officeId: office.id,
                  phoneNumber,
                });
              }}>
              {isPhoneRequestSent ? "Resend" : "Request"}
            </Button>
          </div>
          <div className="relative w-full border-t md:w-96">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform">
              <p className="bg-white px-2 text-xs">OR</p>
            </div>
          </div>
          <div className="flex w-full gap-2 md:w-96">
            <TextField
              placeholder="Input your email"
              containerClassName="grow"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              disabled={!validateEmailAddress(email)}
              className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
              onClick={() => {
                requestCode.mutate({
                  officeId: office.id,
                  email,
                });
              }}>
              {isEmailRequestSent ? "Resend" : "Request"}
            </Button>
          </div>
          {(isPhoneRequestSent || isEmailRequestSent) && (
            <div className="flex w-full gap-2 md:w-96">
              <TextField
                containerClassName="grow"
                placeholder="Input your Verificication code"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              <Button
                disabled={!otp || otp.length !== 6}
                className="rounded-lg border bg-transparent  bg-white text-gray-600 hover:bg-white hover:text-gray-600"
                onClick={() => {
                  verifyCode.mutate({
                    phoneNumber,
                    email,
                    otp,
                  });
                }}>
                Confirm
              </Button>
            </div>
          )}

          {errorMessage && (
            <p className="mt-4 rounded-md bg-red-200 py-2 px-3 text-sm text-red-700">{errorMessage}</p>
          )}
          {/* <div className="border-b border-gray-200" /> */}
        </div>
        <div className="col-span-3 flex flex-col bg-white px-1 py-8 md:px-8">
          <HaveQuestionCard email={office.email} phone={office.phoneNumber} />
        </div>
      </div>
    </BrandedComponentWrapper>
  );
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { user } = querySchema.parse(context.query);

  const env = process.env.FINIX_ENVIRONMENT;
  const appId = process.env.FINIX_APPLICATION_ID;

  const office = await prisma.billingOffice.findUnique({
    where: {
      officename: user,
    },
    select: {
      id: true,
      logo: true,
      name: true,
      officename: true,
      moolahCode: true,
      locationId: true,
      headerColor: true,
      buttonColor: true,
      postPaymentEmail: true,
      moolahCrownMode: true,
      phone: true,
      address1: true,
      address2: true,
      merchantId: true,
      email: true,
      applySurchargeFee: true,
      surchargeOnArPay: true,
      enableFinixACHPayment: true,
      status: true,
      selfMerchant: true,
      showBrandLogoOnLink: true,
    },
  });

  if (!office || office.status === OfficeStatus.INACTIVE) return { notFound: true };

  return {
    props: {
      appId: appId,
      paymentEnv: env,
      office: {
        id: office.id,
        name: office.name,
        logo: office.logo || "",
        officeName: office.officename,
        locationId: office.locationId,
        headerColor: office.headerColor,
        buttonColor: office.buttonColor,
        postPaymentEmail: office.postPaymentEmail,
        phoneNumber: office.phone,
        address1: office.address1,
        address2: office.address2,
        email: office.email,
        showBrandLogoOnLink: office.showBrandLogoOnLink,
      },
    },
  };
};
